import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import classNames from "classnames";
import { Text } from "../Text";
import { NumberField } from "../NumberField";
import { TextAreaField } from "../TextAreaField";
import { TimeField } from "../TimeField";
import { DateField } from "../DateField";
import { DateTimeField } from "../DateTimeField";
import { MultiSelect } from "../MultiSelect";
import { SelectField, Option } from "../SelectField";

const ENTER_KEY = "Enter";
const ESCAPE_KEY = "Escape";

export class EditableText extends React.Component {
  static defaultProps = {
    separator: ", ",
    textType: "P",
    required: false,
    dateFormat: "MMM D, YYYY",
  };

  static propTypes = {
    /** The input that will be rendered when clicked */
    children: PropTypes.node,
    /** Additional class names for the component */
    className: PropTypes.string,
    /** The string format of the Date type when in Text mode */
    dateFormat: PropTypes.string,
    /** The default input value */
    defaultInputValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
      PropTypes.array,
      PropTypes.number,
    ]),
    /** ID of the field */
    id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.array,
      PropTypes.number,
    ]),
    /** A Forward Ref for external UX */
    forwardRef: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.shape({ current: PropTypes.any }),
    ]),
    /** A function to render content other than the text content */
    renderText: PropTypes.func,
    /** Callback for when the value is modified in the input */
    onChange: PropTypes.func,
    /** Callback for when the mode of EditableText is changed back to 'Text' mode */
    onSave: PropTypes.func,
    /** Available options for multiselect and select types */
    options: PropTypes.array,
    /** A boolean value that when set to true shows a message dictating the field must be added to save. */
    required: PropTypes.bool,
    /** numberfield on left side */
    left: PropTypes.bool,
    /** Separator to join (if the value is an Array) */
    separator: PropTypes.string,
    /** Text HTML tag to be rendered */
    textType: PropTypes.string,
    /** Callback search function for selectField and multiselect */
    onSearch: PropTypes.func,
    /** The type of the input field in the EditableText */
    type: PropTypes.oneOf([
      "number",
      "text",
      "date",
      "datetime",
      "multiselect",
      "select",
      "time",
    ]),
    onEdit: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.fieldRef = props.forwardRef || React.createRef();

    this.state = {
      editMode: false,
      textValue: this.convertInputValueToText(props.defaultInputValue),
      inputValue: props.defaultInputValue,
      mouseDownOccured: false,
    };
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
  }

  formatTime(time) {
    let formattedTime = time;
    const hours = +formattedTime.substr(0, 2);
    const formattedHours = hours % 12 || 12;
    const amPm = hours < 12 ? " AM" : " PM";
    formattedTime = formattedHours + formattedTime.substr(2, 3) + amPm;
    return formattedTime;
  }

  convertInputValueToText = (value) => {
    const { dateFormat, separator, type } = this.props;

    if (Array.isArray(value)) {
      return value.join(separator);
    }
    if (type === "time") {
      return this.formatTime(value);
    }

    if (value instanceof Date) {
      return moment(value).format(dateFormat);
    }

    return value;
  };

  isTextHighlighted = () => {
    const selection = window.getSelection().toString();
    return selection.length > 0;
  };

  onClick = (event) => {
    const { editMode, mouseDownOccured } = this.state;
    const notInEditMode = !editMode;
    const linkClicked = event.target.tagName === "A";
    const isMouseDown = event.type === "mousedown";
    const isFocus = event.type === "focus";

    if (linkClicked) {
      event.stopPropagation();
    } else if (isMouseDown) {
      this.setState({ mouseDownOccured: true });
    } else if (isFocus) {
      if (mouseDownOccured) {
        this.setState({ mouseDownOccured: false });
      } else if (notInEditMode) {
        this.setEditModeOn();
      }
    } else if (notInEditMode && !this.isTextHighlighted()) {
      this.setEditModeOn();
    }
  };

  getReturnValue = (value) => {
    const { id } = this.props;

    let returnValue;
    if (id) {
      returnValue = { id, value };
    } else {
      returnValue = value;
    }

    return returnValue;
  };

  onSave = (newValue) => {
    const { onSave } = this.props;

    const returnValue = this.getReturnValue(newValue);

    if (onSave) {
      onSave(returnValue);
    }
  };

  onKeyDown = (event) => {
    if (event.key === ENTER_KEY) {
      this.onClick(event);
    }
    if (event.key === ESCAPE_KEY) {
      this.setEditModeOff();
    }
  };

  validateAndSave = (inputValue) => {
    const { required } = this.props;
    if (required) {
      if (inputValue && inputValue.toString().length > 0) {
        this.turnOffEdit(inputValue);
      }
    } else {
      this.turnOffEdit(inputValue);
    }
  };

  onChange = (newValue) => {
    const { onChange, type } = this.props;
    const textValue = this.convertInputValueToText(newValue);
    const returnValue = this.getReturnValue(newValue);

    if (onChange) {
      onChange(returnValue);
    }

    this.setState({ textValue, inputValue: newValue });

    if (type === "select") {
      this.validateAndSave(newValue);
    }
  };

  handleClickOutside = (event) => {
    if (this.fieldRef && !this.fieldRef.current.contains(event.target)) {
      this.setEditModeOff();
    }
  };

  setEditModeOn = () => {
    const { onEdit } = this.props;
    const { editMode } = this.state;
    if (!editMode) {
      document.addEventListener("mousedown", this.handleClickOutside);
      if (onEdit) {
        onEdit(true);
      }
      this.setState({ editMode: true });
    }
  };

  turnOffEdit = (inputValue) => {
    const { onEdit } = this.props;
    this.onSave(inputValue);
    document.removeEventListener("mousedown", this.handleClickOutside);
    if (onEdit) {
      onEdit(false);
    }
    this.setState({ editMode: false });
  };

  setEditModeOff = () => {
    const { inputValue } = this.state;
    this.validateAndSave(inputValue);
  };

  getClickToEditContent = () => {
    const { required } = this.props;
    const { textValue } = this.state;
    if (required && !textValue) {
      return "This field is required";
    }
    return textValue;
  };

  renderOptions = () => {
    const { options } = this.props;
    return options.map((option) => {
      return (
        <Option value={option} key={option}>
          {option}
        </Option>
      );
    });
  };

  renderInputField = () => {
    const { left, onSearch, type } = this.props;
    const { inputValue } = this.state;

    const inputProps = {
      onChange: this.onChange,
      defaultValue: inputValue,
      defaultOpen: true,
      autoFocus: true,
      open: true,
      onSearch,
      left,
    };

    switch (type) {
      case "number":
        return <NumberField {...inputProps} />;
      case "date":
        return <DateField {...inputProps} />;
      case "datetime":
        return <DateTimeField {...inputProps} />;
      case "multiselect":
        return (
          <MultiSelect {...inputProps}>{this.renderOptions()}</MultiSelect>
        );
      case "select":
        return (
          <SelectField {...inputProps}>{this.renderOptions()}</SelectField>
        );
      case "text":
        return <TextAreaField {...inputProps} />;
      case "time":
        return <TimeField {...inputProps} />;
      default:
        return this.renderText();
    }
  };

  renderText = () => {
    const { renderText, textType } = this.props;

    if (renderText) {
      return renderText();
    }

    return (
      <Text className="EditableText__text" type={textType}>
        {this.getClickToEditContent()}
      </Text>
    );
  };

  handleBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      this.setEditModeOff();
    }
  };

  render() {
    const { className } = this.props;
    const { editMode } = this.state;

    const fieldClassName = classNames("EditableText", className, {
      active: editMode,
    });

    return (
      <div
        className={fieldClassName}
        onMouseDown={this.onClick}
        onClick={this.onClick}
        onKeyDown={this.onKeyDown}
        tabIndex={0}
        role="button"
        ref={this.fieldRef}
        onFocus={this.onClick}
        onBlur={this.handleBlur}
      >
        {editMode ? this.renderInputField() : this.renderText()}
      </div>
    );
  }
}

export default EditableText;
